import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { db } from '../firebase';
import { collection, getDocs, query, doc, writeBatch, orderBy } from 'firebase/firestore';
import { Container, Card, Form, Button, Spinner, Alert, Table, Tabs, Tab, Row, Col, Badge } from 'react-bootstrap';
import { Link } from 'react-router-dom';

// 発注番号をハイフン区切りにフォーマットするヘルパー関数
const formatDisplayOrderNumber = (orderNumber) => {
  if (!orderNumber || orderNumber.length < 13) return orderNumber;
  const datePart = orderNumber.substring(0, 8);
  const dailyPart = orderNumber.substring(8, 11);
  const destPart = orderNumber.substring(11);
  return `${datePart}-${dailyPart}-${destPart}`;
};

// YYYY-MM-DD形式の文字列をYYYY/MM/DD形式に変換するヘルパー関数
const formatShippingDate = (dateStr) => {
    if (!dateStr || dateStr.length !== 10) return dateStr;
    const parts = dateStr.split('-');
    if (parts.length !== 3) return dateStr;
    return `${parts[0]}/${parts[1]}/${parts[2]}`;
};

// 注文ステータスの表示名と色を定義
const statusMap = {
  new: { text: '新規', bg: 'primary' },
  processed: { text: '受付済み', bg: 'success' },
  shipped: { text: '出荷準備済み', bg: 'info' }
};

function ShipperOrderHistory() {
  const [orders, setOrders] = useState([]);
  const [usersMap, setUsersMap] = useState(new Map());
  const [companiesMap, setCompaniesMap] = useState(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const [inputData, setInputData] = useState({});
  const [updating, setUpdating] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [usersSnapshot, companiesSnapshot] = await Promise.all([
        getDocs(collection(db, 'users')),
        getDocs(collection(db, 'companies'))
      ]);

      const usersDataMap = new Map();
      usersSnapshot.forEach(doc => usersDataMap.set(doc.id, doc.data()));
      setUsersMap(usersDataMap);

      const companiesDataMap = new Map();
      companiesSnapshot.forEach(doc => companiesDataMap.set(doc.id, doc.data()));
      setCompaniesMap(companiesDataMap);

      const ordersCollectionRef = collection(db, 'orders');
      const q = query(ordersCollectionRef, orderBy('orderedAt', 'desc'));
      const data = await getDocs(q);
      const fetchedOrders = data.docs.map(doc => ({ ...doc.data(), id: doc.id }));
      setOrders(fetchedOrders);
    } catch (err) {
      setError('履歴データの取得に失敗しました。');
      console.error(err);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    document.title = 'イレギュラー対応用全注文履歴 | My Order System';
    fetchData();
  }, [fetchData]);

  const filteredOrders = useMemo(() => {
    return orders.filter(order => {
      const orderDate = order.orderedAt?.toDate();
      if (startDate && orderDate) {
        if (orderDate < new Date(startDate)) return false;
      }
      if (endDate && orderDate) {
        const endOfDay = new Date(endDate);
        endOfDay.setHours(23, 59, 59, 999);
        if (orderDate > endOfDay) return false;
      }
      if (!searchTerm.trim()) return true;
      const lowercasedFilter = searchTerm.toLowerCase();
      const customerName = usersMap.get(order.orderedByUid)?.personName || '';
      const hasMatchingDestination = order.shippingDestinations.some(dest =>
        (dest.recipientName || '').toLowerCase().includes(lowercasedFilter) ||
        (dest.phoneNumber || '').toLowerCase().includes(lowercasedFilter) ||
        (dest.orderNumber || '').toLowerCase().includes(lowercasedFilter)
      );
      return customerName.toLowerCase().includes(lowercasedFilter) || hasMatchingDestination;
    });
  }, [orders, searchTerm, startDate, endDate, usersMap]);

  const pendingDestinations = useMemo(() => 
    filteredOrders
      .filter(o => o.status === 'new' || o.status === 'processed')
      .flatMap(order => order.shippingDestinations.map(dest => ({ ...order, dest })))
  , [filteredOrders]);

  const shippedDestinations = useMemo(() => 
    filteredOrders
      .filter(o => o.status === 'shipped')
      .flatMap(order => order.shippingDestinations.map(dest => ({ ...order, dest })))
  , [filteredOrders]);

  const handleInputChange = (orderNumber, field, value) => {
    setInputData(prev => ({
      ...prev,
      [orderNumber]: {
        ...prev[orderNumber],
        [field]: value,
      },
    }));
  };

  const handleUpdate = async () => {
    setError('');
    setSuccess('');
    const updatesToPerform = Object.entries(inputData).filter(([_, data]) => data.trackingNumber !== undefined || data.carrier !== undefined);

    if (updatesToPerform.length === 0) {
      setError('更新する情報が入力されていません。');
      return;
    }
    setUpdating(true);

    try {
        const batch = writeBatch(db);
        let updatedCount = 0;
        let validationError = '';

        const updatesByParentId = updatesToPerform.reduce((acc, [orderNumber, data]) => {
            const parentOrder = orders.find(o => o.shippingDestinations.some(d => d.orderNumber === orderNumber));
            if (parentOrder) {
                if (!acc[parentOrder.id]) {
                    acc[parentOrder.id] = { parentOrder, updates: [] };
                }
                acc[parentOrder.id].updates.push({ orderNumber, ...data });
            }
            return acc;
        }, {});

        for (const orderId in updatesByParentId) {
            const { parentOrder, updates } = updatesByParentId[orderId];

            const carrierUpdate = updates.find(u => u.carrier !== undefined);
            const newCarrier = carrierUpdate ? carrierUpdate.carrier : parentOrder.shippingCarrier;

            const newDestinations = parentOrder.shippingDestinations.map(dest => {
                const update = updates.find(u => u.orderNumber === dest.orderNumber);
                if (update && update.trackingNumber !== undefined) {
                    return { ...dest, trackingNumber: update.trackingNumber };
                }
                return dest;
            });

            if (parentOrder.status !== 'shipped') {
                for (const update of updates) {
                    const dest = newDestinations.find(d => d.orderNumber === update.orderNumber);
                    if (!dest.trackingNumber || !newCarrier) {
                        validationError = `発注番号 ${formatDisplayOrderNumber(dest.orderNumber)} の送り状番号と配送会社の両方を入力してください。`;
                        break;
                    }
                }
            }
            if (validationError) break;

            const allDestinationsNowShipped = newDestinations.every(d => d.trackingNumber);
            const newStatus = (allDestinationsNowShipped && newCarrier) ? 'shipped' : parentOrder.status;

            batch.update(doc(db, 'orders', orderId), {
                shippingDestinations: newDestinations,
                status: newStatus,
                shippingCarrier: newCarrier,
            });
            updatedCount += updates.length;
        }

        if (validationError) {
            setError(validationError);
        } else {
            await batch.commit();
            setSuccess(`${updatedCount}件の出荷情報を更新しました。`);
            setInputData({});
            fetchData();
        }
    } catch (err) {
        setError('データベースの更新中にエラーが発生しました。');
        console.error(err);
    }
    setUpdating(false);
  };

  const renderEditableTable = (destList, isShippedTab) => {
    return (
      <Table striped bordered hover responsive className="mt-3">
        <thead>
          <tr>
            <th>発注番号</th>
            <th>発注者</th>
            <th>送付先</th>
            <th>出荷予定日</th>
            <th>送り状No.</th>
            <th>配送会社</th>
            <th>ステータス</th>
          </tr>
        </thead>
        <tbody>
          {destList.map(item => {
            const { dest, ...order } = item;
            const statusInfo = statusMap[order.status] || { text: order.status, bg: 'secondary' };
            const customer = usersMap.get(order.orderedByUid);
            const currentTracking = inputData[dest.orderNumber]?.trackingNumber ?? dest.trackingNumber ?? '';
            const currentCarrier = inputData[dest.orderNumber]?.carrier ?? order.shippingCarrier ?? '';

            return (
              <tr key={dest.orderNumber}>
                <td>{formatDisplayOrderNumber(dest.orderNumber)}</td>
                <td>{customer?.personName || '不明'}</td>
                <td>{dest.recipientName} 様</td>
                <td>{formatShippingDate(order.estimatedShippingDate)}</td>
                <td>
                  <Form.Control
                    type="text"
                    size="sm"
                    value={currentTracking}
                    onChange={(e) => handleInputChange(dest.orderNumber, 'trackingNumber', e.target.value)}
                    placeholder={isShippedTab ? '' : '入力必須'}
                  />
                </td>
                <td>
                  <Form.Select
                    size="sm"
                    value={currentCarrier}
                    onChange={(e) => handleInputChange(dest.orderNumber, 'carrier', e.target.value)}
                  >
                    <option value="">{isShippedTab ? '未選択' : '選択必須'}</option>
                    <option value="yupri">日本郵便</option>
                    <option value="istar">福山通運</option>
                  </Form.Select>
                </td>
                <td><Badge bg={statusInfo.bg}>{statusInfo.text}</Badge></td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    );
  };

  return (
    <main>
      <Container className="my-4">
        <Card className="shadow-sm">
          <Card.Header as="h2" className="p-3">イレギュラー対応用全注文履歴</Card.Header>
          <Card.Body className="p-4">
            {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
            {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible>{success}</Alert>}
            
            <Card className="mb-4 bg-light">
              <Card.Body>
                <h5 className="mb-3">検索条件</h5>
                <Row className="g-2">
                  <Col md={12}>
                    <Form.Control type="text" placeholder="発注番号、発注者名、送付先名で検索..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                  </Col>
                  <Col md={5}><Form.Control type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} /></Col>
                  <Col md={5}><Form.Control type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} /></Col>
                  <Col md={2}><div className="d-grid"><Button variant="secondary" onClick={() => { setSearchTerm(''); setStartDate(''); setEndDate(''); }}>クリア</Button></div></Col>
                </Row>
              </Card.Body>
            </Card>

            <div className="d-flex justify-content-end mb-3">
                <Button onClick={handleUpdate} disabled={updating || Object.keys(inputData).length === 0}>
                    {updating ? <Spinner as="span" size="sm" /> : '入力した情報を一括更新'}
                </Button>
            </div>

            {loading ? <div className="text-center"><Spinner animation="border" /></div> : (
              <Tabs defaultActiveKey="pending" id="history-tabs" className="mb-3">
                <Tab eventKey="pending" title={`送り状NO.未発行 (${pendingDestinations.length})`}>
                  {renderEditableTable(pendingDestinations, false)}
                </Tab>
                <Tab eventKey="shipped" title={`送り状NO.発行済 (${shippedDestinations.length})`}>
                  {renderEditableTable(shippedDestinations, true)}
                </Tab>
              </Tabs>
            )}

            {!loading && orders.length === 0 && <p className="text-muted">注文履歴はありません。</p>}
            
            <hr className="my-4" />
            <Link to="/shipper-menu">出荷者メニューに戻る</Link>
          </Card.Body>
        </Card>
      </Container>
    </main>
  );
}

export default ShipperOrderHistory;
